import * as React from 'react'
import classNames from 'classnames'
import { when } from 'mobx'
import style from './teams-page.css'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { Button, Empty, Heading, Icons, SearchInputField } from 'plume-ui'
import { DashboardLayout } from 'modules/dashboard/common/dashboard-layout'
import { useStores } from 'utils/hooks/useStores'
import { Loader } from 'components/loader'
import { Tooltip } from 'components/tooltip'
import { Team } from 'interfaces/api/portal/company-api'
import { TEAMS_ROUTE } from 'constants/routes'
import { SetTeam } from './add-edit-team'
import { displayConfirmDeleteTeam } from './confirm-delete-team'

export const TeamsPage = observer(() => {
  const appStore = useStores()
  const { t } = useTranslation()
  const history = useHistory()
  const { teamId } = useParams<{ teamId?: string }>()
  const accountStore = appStore.accountStore
  const currentUser = appStore.authStore?.currentUser

  const [search, setSearch] = React.useState('')
  const [isAdding, setIsAdding] = React.useState(false)
  const rightPaneRef = React.useRef<HTMLDivElement>(null)

  const teams = accountStore?.teams ?? null
  const isLoadingModal = accountStore?.isLoadingModal
  const lastAddedTeamGroupId = accountStore?.lastAddedTeamGroupId

  React.useEffect(() => {
    // load current user + company on direct page load (matches other top-level pages)
    appStore.authStore.update()
    // accountStore is created lazily once the user + company are loaded
    const disposer = when(
      () => !!appStore.authStore.currentUser?.company && !!appStore.accountStore,
      () => appStore.accountStore?.initManageTeams(),
    )
    return () => {
      disposer()
      appStore.accountStore?.clearManageTeamsFormState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedTeam = teamId ? teams?.find(team => team.groupId === teamId) : undefined

  // isLoadingModal drops between the teams-list and team-apps loads, so wait on both.
  const isLoadingSelectedTeamApps =
    !!selectedTeam && accountStore?.loadedTeamAppsGroupId !== selectedTeam.groupId
  // A team-apps fetch only affects the right pane; save/delete/initial load cover both panes.
  const isLoadingPage = isLoadingModal && !isLoadingSelectedTeamApps

  // After a new team is created, switch to it in edit mode instead of leaving the add form open
  React.useEffect(() => {
    if (lastAddedTeamGroupId) {
      setIsAdding(false)
      history.push(`${TEAMS_ROUTE}/${lastAddedTeamGroupId}`)
      accountStore.setLastAddedTeamGroupId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAddedTeamGroupId])

  // Reset the right pane scroll position when switching teams or opening the add form
  React.useEffect(() => {
    rightPaneRef.current?.scrollTo({ top: 0 })
  }, [teamId, isAdding])

  if (!accountStore) {
    return (
      <DashboardLayout currentUser={currentUser}>
        <Loader />
      </DashboardLayout>
    )
  }

  const selectTeam = (team: Team) => {
    setIsAdding(false)
    history.push(`${TEAMS_ROUTE}/${team.groupId}`)
  }

  const startAddTeam = () => {
    setIsAdding(true)
    history.push(TEAMS_ROUTE)
  }

  const filteredTeams = (teams || []).filter(team =>
    team.name?.toLowerCase().includes(search.toLowerCase()),
  )

  const renderRightPane = () => {
    if (isAdding) {
      return <SetTeam key="add" action="add" accountStore={accountStore} />
    }
    if (selectedTeam) {
      return (
        <SetTeam
          key={selectedTeam.groupId}
          action="edit"
          team={selectedTeam}
          accountStore={accountStore}
          onDelete={() => {
            if (selectedTeam.name && selectedTeam.groupId) {
              displayConfirmDeleteTeam(
                appStore.actionModalStore,
                accountStore,
                selectedTeam.name,
                selectedTeam.groupId,
                t,
              )
            }
          }}
        />
      )
    }
    return (
      // @ts-expect-error - Empty accepts children but its types error across ts versions
      <Empty
        icon={<Icons.GroupIcon width="28px" height="28px" />}
        iconWidth={64}
        label={t('teams.selectTeam')}
      >
        <div className={style.emptyStateDescription}>{t('teams.selectTeamDescription')}</div>
        <Button
          styleVariant="secondary"
          icon={<Icons.PlusIcon width="16px" height="16px" />}
          onClick={startAddTeam}
          disabled={!teams}
        >
          {t('teams.addTeam')}
        </Button>
      </Empty>
    )
  }

  return (
    <DashboardLayout currentUser={currentUser}>
      <div className={style.root}>
        <div className={style.headerRow}>
          <div className={style.headerTitles}>
            <Heading level={2}>{t('teams.manageTeams')}</Heading>
            <span className={style.subtitle}>{t('teams.manageTeamsSubtitle')}</span>
          </div>
          <Tooltip
            placement="top"
            overlay={<span>{t('teams.scrollDownToSave')}</span>}
            align={{ offset: [0, -5] }}
            trigger={isAdding ? ['hover'] : []}
            mouseLeaveDelay={0}
          >
            <span className={style.addTeamButtonWrapper}>
              <Button
                styleVariant="secondary"
                icon={<Icons.PlusIcon width="16px" height="16px" />}
                onClick={startAddTeam}
                disabled={isAdding || !teams}
              >
                {t('teams.addTeam')}
              </Button>
            </span>
          </Tooltip>
        </div>

        <div className={style.panes}>
          <div className={style.leftPane}>
            <div className={style.searchWrapper}>
              <SearchInputField
                type="text"
                value={search}
                placeholder={t('teams.searchTeams')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              />
            </div>
            <div className={style.teamList}>
              {filteredTeams.map(team => (
                <div
                  key={team.groupId}
                  className={classNames(
                    style.teamItem,
                    selectedTeam?.groupId === team.groupId && style.teamItemSelected,
                  )}
                  onClick={() => selectTeam(team)}
                >
                  {team.isLocked && (
                    <Tooltip placement="top" overlay={<span>{t('teams.samlTeam')}</span>}>
                      <Icons.KeySolidIcon width="14px" height="14px" className={style.lockedIcon} />
                    </Tooltip>
                  )}
                  <span className={style.teamItemName}>{team.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            ref={rightPaneRef}
            className={classNames(
              style.rightPane,
              (isLoadingPage || isLoadingSelectedTeamApps) && style.rightPaneLoading,
            )}
          >
            {renderRightPane()}
            {/* SetTeam starts the apps fetch on mount, so overlay it rather than replace it. */}
            {isLoadingSelectedTeamApps && <Loader rootClassName={style.rightPaneLoader} />}
          </div>

          {/*
            Loading overlay for the whole panes area. .panes is bounded to the
            viewport height (.root is calc(100dvh - 160px) and the panes scroll
            internally), so the centered spinner is always visible and on top
            regardless of how tall the team's app table is — and it never covers the
            left navigation, which sits outside .root.
          */}
          {isLoadingPage && <Loader rootClassName={style.panesLoader} />}
        </div>
      </div>
    </DashboardLayout>
  )
})
